import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import ActivityFeed from "@/components/ActivityFeed";
import DemoModePanel from "@/components/DemoModePanel";
import AdminLemonBotPanel from "@/components/AdminLemonBotPanel";
import AdminPromptHistoryPanel from "@/components/AdminPromptHistoryPanel";
import AdminSyncPanel from "@/components/AdminSyncPanel";
import SchoolBrandingPanel from "@/components/SchoolBrandingPanel";
import EmailStatusBadge from "@/components/EmailStatusBadge";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import { Users, UserPlus, Mail, KeyRound, BarChart3, Loader2, RefreshCw, Database, TrendingUp, Activity, Clapperboard, Bot, History } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import ChartSummary from "@/components/ChartSummary";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminDashboard({ user, onLogout }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const validTab = (t) =>
    ["activity", "analytics", "demo", "lemonbot", "prompts", "system"].includes(t) ? t : null;

  const [activeTab, setActiveTab] = useState(
    () => validTab(searchParams.get("tab")) || "activity"
  );
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // URL → state: when the search params change (e.g., navbar badge clicked
  // while already on /admin), sync the active tab. We never write back from
  // here to avoid a feedback loop.
  useEffect(() => {
    const fromUrl = validTab(searchParams.get("tab"));
    if (fromUrl && fromUrl !== activeTab) {
      setActiveTab(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // State → URL: only fired when the user explicitly clicks a tab button.
  const switchTab = (tabId) => {
    setActiveTab(tabId);
    const next = new URLSearchParams(searchParams);
    next.set("tab", tabId);
    setSearchParams(next, { replace: true });
  };

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/registration-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "analytics" && !stats) fetchStats();
  }, [activeTab]);

  const handleSeedData = async () => {
    if (!window.confirm("This will seed the database with initial modules and lessons. Continue?")) return;
    setLoading(true);
    setMessage("");
    try {
      const response = await axios.post(`${API}/seed-data`);
      setMessage(response.data.message || "Data seeded successfully!");
    } catch (err) {
      setMessage(err.response?.data?.detail || "Failed to seed data");
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: "activity", label: "Activity Feed", icon: Activity },
    { id: "analytics", label: "Registration Analytics", icon: BarChart3 },
    { id: "demo", label: "Demo Mode", icon: Clapperboard },
    { id: "lemonbot", label: "LemonBot", icon: Bot },
    { id: "prompts", label: "Prompt History", icon: History },
    { id: "system", label: "System", icon: Database },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-6xl mx-auto px-4 py-6">
        <AnnouncementBanner role={user?.role} />
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" data-testid="welcome-message">Admin Dashboard</h1>
            <p className="text-gray-500 mt-1 text-sm">Platform analytics, activity feed & system management</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => window.location.href = "/admin/announcements"}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 text-xs font-semibold"
              data-testid="admin-open-announcements"
            >
              📢 Announcements
            </button>
            <button
              type="button"
              onClick={() => window.location.href = "/admin/leads"}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-yellow-300 bg-yellow-50 text-yellow-800 hover:bg-yellow-100 text-xs font-semibold"
              data-testid="admin-open-leads"
            >
              🍋 Pilot Leads
            </button>
            <button
              type="button"
              onClick={() => window.location.href = "/admin/demo-reports"}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-orange-300 bg-orange-50 text-orange-800 hover:bg-orange-100 text-xs font-semibold"
              data-testid="admin-open-demo-reports"
            >
              ✨ Demo Reports
            </button>
            <button
              type="button"
              onClick={() => window.location.href = "/admin/ceo-challenge"}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-purple-300 bg-purple-50 text-purple-800 hover:bg-purple-100 text-xs font-semibold"
              data-testid="admin-open-ceo-challenge"
            >
              🏆 CEO Challenge
            </button>
            <EmailStatusBadge />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white rounded-xl border border-gray-200 p-1 overflow-x-auto" data-testid="admin-tabs">
          {tabs.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => switchTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === t.id
                    ? "bg-gray-900 text-white shadow-sm"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === "activity" && <ActivityFeed />}

        {activeTab === "analytics" && (
          <div>
            <div className="flex justify-end mb-4">
              <button
                onClick={fetchStats}
                disabled={statsLoading}
                data-testid="refresh-stats-btn"
                className="flex items-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-all"
              >
                <RefreshCw className={`w-4 h-4 ${statsLoading ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>

            {statsLoading ? (
              <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
            ) : stats ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  <StatCard icon={<Users className="w-5 h-5" />} label="Total Users" value={stats.total_users} color="blue" testId="stat-total-users" />
                  <StatCard icon={<UserPlus className="w-5 h-5" />} label="Students" value={stats.by_role?.student || 0} color="green" testId="stat-students" />
                  <StatCard icon={<Mail className="w-5 h-5" />} label="Email Signups" value={stats.by_login_method?.email || 0} color="purple" testId="stat-email-signups" />
                  <StatCard icon={<KeyRound className="w-5 h-5" />} label="Class Code Signups" value={stats.by_login_method?.class_code || 0} color="amber" testId="stat-classcode-signups" />
                </div>

                {stats.by_role && Object.keys(stats.by_role).length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8" role="region" aria-labelledby="users-by-role-h">
                    <h2 id="users-by-role-h" className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-gray-500" aria-hidden="true" />Users by Role
                    </h2>
                    <ChartSummary
                      testId="users-by-role-summary"
                      title="Users by Role"
                      items={Object.entries(stats.by_role).map(([role, count]) => ({ label: role, value: count }))}
                    />
                    <div className="space-y-3" role="img" aria-label={`${Object.keys(stats.by_role).length} roles, total ${stats.total_users} users`}>
                      {Object.entries(stats.by_role).map(([role, count]) => (
                        <div key={role} className="flex items-center gap-3">
                          <span className="text-sm font-medium text-gray-600 w-24 capitalize">{role}</span>
                          <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                            <div className="h-full bg-blue-500 rounded-full flex items-center justify-end pr-2"
                              style={{ width: `${Math.max(8, (count / stats.total_users) * 100)}%` }}>
                              <span className="text-xs font-bold text-white">{count}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {stats.daily_signups?.length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8" role="region" aria-labelledby="signup-trend-h">
                    <h2 id="signup-trend-h" className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-gray-500" aria-hidden="true" />Signup Trend (Last 30 Days)
                    </h2>
                    {(() => {
                      const ds = stats.daily_signups || [];
                      const total = ds.reduce((s, d) => s + (d.count || 0), 0);
                      const peak = ds.reduce((m, d) => (d.count || 0) > m.count ? { date: d.date, count: d.count } : m, { count: 0 });
                      return (
                        <p className="sr-only" data-testid="signup-trend-summary">
                          Signup trend: {ds.length} days · {total} signups total · peak on {peak.date} with {peak.count} signups.
                        </p>
                      );
                    })()}
                    <div className="h-64" data-testid="signup-trend-chart" role="img" aria-label="Area chart showing daily signup counts over the last 30 days">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={stats.daily_signups.map(d => ({
                          date: new Date(d.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
                          signups: d.count
                        }))}>
                          <defs>
                            <linearGradient id="signupGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#9ca3af" }} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#9ca3af" }} />
                          <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e5e7eb", fontSize: "13px" }} labelStyle={{ fontWeight: 600 }} />
                          <Area type="monotone" dataKey="signups" stroke="#3b82f6" strokeWidth={2} fill="url(#signupGradient)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Recent Registrations */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4" data-testid="recent-registrations-title">Recent Registrations</h2>
                  {stats.recent_users?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="registrations-table">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-2 px-3 text-gray-500 font-medium">Name</th>
                            <th className="text-left py-2 px-3 text-gray-500 font-medium">Email</th>
                            <th className="text-left py-2 px-3 text-gray-500 font-medium">Role</th>
                            <th className="text-left py-2 px-3 text-gray-500 font-medium">Method</th>
                            <th className="text-left py-2 px-3 text-gray-500 font-medium">Registered</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stats.recent_users.map((u, i) => (
                            <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-2.5 px-3 font-medium text-gray-900">{u.name || u.first_name || "—"}</td>
                              <td className="py-2.5 px-3 text-gray-600">{u.email || <span className="text-amber-600 text-xs font-medium">Class Code</span>}</td>
                              <td className="py-2.5 px-3">
                                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                                  u.role === "admin" ? "bg-red-100 text-red-700" : u.role === "educator" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"
                                }`}>{u.role || "student"}</span>
                              </td>
                              <td className="py-2.5 px-3 text-gray-500 text-xs">{u.email ? "Email" : "Class Code"}</td>
                              <td className="py-2.5 px-3 text-gray-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-400 text-center py-8">No registrations yet</p>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-16 text-gray-400">
                <button onClick={fetchStats} className="text-blue-600 hover:underline">Load analytics</button>
              </div>
            )}
          </div>
        )}

        {activeTab === "demo" && <DemoModePanel />}

        {activeTab === "lemonbot" && <AdminLemonBotPanel />}

        {activeTab === "prompts" && <AdminPromptHistoryPanel />}

        {activeTab === "system" && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-gray-500" />Database Management
            </h2>
            {message && (
              <div className={`mb-4 p-3 rounded-lg text-sm ${message.includes("Failed") ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"}`} data-testid="message">
                {message}
              </div>
            )}
            <div className="border border-gray-200 rounded-lg p-5">
              <h3 className="font-semibold text-gray-800 mb-1">Seed Initial Data</h3>
              <p className="text-sm text-gray-500 mb-3">Create the 7 modules and sample lessons from The Lemonade Stand Business Plan curriculum.</p>
              <button
                onClick={handleSeedData}
                disabled={loading}
                className="bg-gray-900 hover:bg-gray-800 disabled:bg-gray-400 text-white px-5 py-2 rounded-lg text-sm font-medium transition-all"
                data-testid="seed-data-btn"
              >
                {loading ? "Seeding..." : "Seed Database"}
              </button>
            </div>
            <AdminSyncPanel />
            <SchoolBrandingPanel />
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color, testId }) {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-600 border-blue-100",
    green: "bg-green-50 text-green-600 border-green-100",
    purple: "bg-purple-50 text-purple-600 border-purple-100",
    amber: "bg-amber-50 text-amber-600 border-amber-100"
  };
  return (
    <div className={`rounded-xl border p-5 ${colorClasses[color]}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-2 opacity-70">{icon}<span className="text-xs font-medium">{label}</span></div>
      <div className="text-3xl font-bold">{value}</div>
    </div>
  );
}

export default AdminDashboard;
